import React, { useState, useEffect } from 'react';
import { Modal } from "../../../components/ui/modal";
import Button from "../../../components/ui/button/Button";
import Input from "../../../components/form/input/InputField";
import Select from "../../../components/form/Select";
import { Calendar, DollarSign, FileText, CreditCard, AlertCircle, ClipboardList, Tag } from 'lucide-react';
import useApi from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';
import { Nota, NotaFormValues } from '../types/index';
import { format } from 'date-fns';

interface CreateNotaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  notaToEdit?: Nota;
}

interface FormErrors {
  fecha?: string;
  facturaId?: string;
  tipo?: string;
  numero?: string;
  monto?: string;
  motivo?: string;
  descripcion?: string;
}

// Initial form state
const initialFormState: NotaFormValues = {
  facturaId: 0,
  tipo: 'credito',
  numero: '',
  fecha: format(new Date(), 'yyyy-MM-dd'),
  monto: 0,
  motivo: 'error',
  descripcion: '',
  impactoSaldo: 0
};

const CreateNotaModal: React.FC<CreateNotaModalProps> = ({ isOpen, onClose, onSuccess, notaToEdit }) => {
  // Get API methods
  const { createNota, updateNota, getFacturas } = useApi();
  
  // Add search state for facturas
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{id: number, numero: string, clienteNombre: string, saldoPendiente?: number}>>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState<{id: number, numero: string, clienteNombre: string, saldoPendiente?: number} | null>(null);
  
  // Original state
  const [facturas, setFacturas] = useState<{ label: string; value: string }[]>([]);
  const [form, setForm] = useState<NotaFormValues>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Load facturas when the modal opens
  useEffect(() => {
    if (isOpen) {
      loadFacturas();
      
      if (notaToEdit) {
        // Format the date for the input field
        const formattedDate = notaToEdit.fecha ? format(new Date(notaToEdit.fecha), 'yyyy-MM-dd') : '';
        
        setForm({
          id: notaToEdit.id,
          facturaId: notaToEdit.facturaId,
          tipo: notaToEdit.tipo,
          numero: notaToEdit.numero,
          fecha: formattedDate,
          monto: notaToEdit.monto,
          motivo: notaToEdit.motivo,
          motivoDetalle: notaToEdit.motivoDetalle,
          descripcion: notaToEdit.descripcion,
          impactoSaldo: notaToEdit.impactoSaldo
        });
      } else {
        // Generate a random number for the new nota
        const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        const tipoPrefix = initialFormState.tipo === 'credito' ? 'NC' : 'ND';
        const newNumero = `${tipoPrefix}-${randomNum}`;
        
        setForm({
          ...initialFormState,
          numero: newNumero,
          fecha: format(new Date(), 'yyyy-MM-dd')
        });
      }
    }
  }, [isOpen, notaToEdit]);
  
  // Set the selected factura when facturas are loaded or when editing
  useEffect(() => {
    if (notaToEdit && facturas.length > 0) {
      const facturaValue = notaToEdit.facturaId.toString();
      const facturaOption = facturas.find(f => f.value === facturaValue);
      
      if (facturaOption) {
        // Instead of setting the option directly, fetch the actual factura data
        const fetchFacturaDetails = async () => {
          try {
            const allFacturas = await getFacturas();
            const matchingFactura = allFacturas.find(f => f.id === notaToEdit.facturaId);
            
            if (matchingFactura) {
              setSelectedFactura({
                id: matchingFactura.id!,
                numero: matchingFactura.numero,
                clienteNombre: matchingFactura.clienteNombre || 'Cliente sin nombre',
                saldoPendiente: matchingFactura.saldoPendiente || 0
              });
              setSearchTerm(matchingFactura.numero);
            }
          } catch (error) {
            console.error('Error fetching factura details:', error);
          }
        };
        
        fetchFacturaDetails();
      }
    }
  }, [notaToEdit, facturas, getFacturas]);
  
  // Update the numero when tipo changes
  useEffect(() => {
    // Only update if the component is mounted, not editing, and the tipo has changed
    if (!notaToEdit && form.tipo && isOpen) {
      // Track if we need a new number
      const tipoPrefix = form.tipo === 'credito' ? 'NC' : 'ND';
      const currentPrefix = form.numero.substring(0, 2);
      
      // Only generate a new number if the tipo has changed (prefix doesn't match)
      if (currentPrefix !== tipoPrefix) {
        const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        const newNumero = `${tipoPrefix}-${randomNum}`;
        
        setForm((prev: NotaFormValues) => ({
          ...prev,
          numero: newNumero
        }));
      }
    }
  }, [form.tipo, notaToEdit, isOpen, form.numero]);
  
  // Update impacto saldo when tipo or monto changes
  useEffect(() => {
    const impacto = form.tipo === 'credito' ? -form.monto : form.monto;
    setForm((prev: NotaFormValues) => ({
      ...prev,
      impactoSaldo: impacto
    }));
  }, [form.tipo, form.monto]);
  
  // Modify the loadFacturas function to only fetch data, not create select options
  const loadFacturas = async () => {
    try {
      const data = await getFacturas();
      // Save the raw factura data, we'll use it for search
      setFacturas(
        data.map(factura => ({
          label: `${factura.numero} - ${factura.clienteNombre || 'Sin nombre'}`,
          value: factura.id?.toString() || ''
        }))
      );
    } catch (error) {
      console.error('Error loading facturas:', error);
      toast.error(
        'Error al cargar las facturas',
        {
          duration: 3000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    }
  };

  // Add a new function for handling search
  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    
    if (value.length < 2) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }
    
    setLoadingSearch(true);
    setShowSuggestions(true);
    
    try {
      // Fetch facturas that match the search term
      const facturas = await getFacturas();
      
      const filteredFacturas = facturas
        .filter(factura => 
          factura.numero.toLowerCase().includes(value.toLowerCase()) ||
          (factura.clienteNombre && factura.clienteNombre.toLowerCase().includes(value.toLowerCase()))
        )
        .filter(factura => factura.id !== undefined) // Ensure id exists
        .map(factura => ({
          id: factura.id!,
          numero: factura.numero,
          clienteNombre: factura.clienteNombre || 'Cliente sin nombre',
          saldoPendiente: factura.saldoPendiente || 0
        }));
      
      setSearchResults(filteredFacturas);
    } catch (error) {
      console.error('Error searching facturas', error);
      toast.error(
        'Error al buscar facturas',
        {
          duration: 3000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    } finally {
      setLoadingSearch(false);
    }
  };
  
  // Add a function to handle factura selection
  const handleSelectFactura = (factura: {id: number, numero: string, clienteNombre: string, saldoPendiente?: number}) => {
    setSelectedFactura(factura);
    setSearchTerm(factura.numero);
    setForm(prev => ({
      ...prev,
      facturaId: factura.id
    }));
    setShowSuggestions(false);
  };
  
  // Validate form before submission
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!form.fecha) {
      newErrors.fecha = "La fecha es requerida";
    }
    
    if (!form.facturaId || form.facturaId <= 0) {
      newErrors.facturaId = "Debe seleccionar una factura";
    }
    
    if (!form.tipo) {
      newErrors.tipo = "El tipo de nota es requerido";
    }
    
    if (!form.numero) {
      newErrors.numero = "El número de nota es requerido";
    }
    
    if (!form.monto || form.monto <= 0) {
      newErrors.monto = "El monto debe ser mayor a cero";
    }
    
    if (!form.motivo) {
      newErrors.motivo = "El motivo es requerido";
    }
    
    if (!form.descripcion) {
      newErrors.descripcion = "La descripción es requerida";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'monto') {
      // Handle numeric input
      const numValue = parseFloat(value);
      setForm({
        ...form,
        [name]: isNaN(numValue) ? 0 : numValue
      });
    } else {
      setForm({
        ...form,
        [name]: value
      });
    }
    
    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  
  // Handle select changes
  const handleSelectChange = (value: string, name: string) => {
    if (name === 'facturaId') {
      const facturaId = parseInt(value);
      const facturaValue = value;
      const facturaOption = facturas.find(f => f.value === facturaValue);
      
      setForm({
        ...form,
        [name]: facturaId
      });
      
      // If we need to update selectedFactura, we need to fetch the actual factura
      // This would typically be done through an API call or by using cached data
      // For demonstration, we'll just set a placeholder
      if (facturaOption) {
        // In a real implementation, you'd fetch the full factura details here
        const fetchFacturaDetails = async () => {
          try {
            const allFacturas = await getFacturas();
            const matchingFactura = allFacturas.find(f => f.id === facturaId);
            
            if (matchingFactura) {
              setSelectedFactura({
                id: matchingFactura.id!,
                numero: matchingFactura.numero,
                clienteNombre: matchingFactura.clienteNombre || 'Cliente sin nombre',
                saldoPendiente: matchingFactura.saldoPendiente || 0
              });
              setSearchTerm(matchingFactura.numero);
            }
          } catch (error) {
            console.error('Error fetching factura details:', error);
          }
        };
        
        fetchFacturaDetails();
      }
    } else {
      setForm({
        ...form,
        [name]: value
      });
    }
    
    // Clear any existing error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  
  // Reset form state
  const resetForm = () => {
    setForm(initialFormState);
    setErrors({});
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Ensure numeric values are correctly parsed
    const formData = {
      ...form,
      facturaId: Number(form.facturaId),
      monto: Number(form.monto),
      impactoSaldo: form.tipo === 'credito' ? -Number(form.monto) : Number(form.monto)
    };
    
    console.log("[DEBUG-FRONTEND] Creating nota with data:", JSON.stringify(formData, null, 2));
    
    try {
      setIsLoading(true);
      
      if (form.id) {
        // Update existing nota
        console.log("[DEBUG-FRONTEND] Updating existing nota:", form.id);
        const response = await updateNota(form.id, formData);
        console.log("[DEBUG-FRONTEND] Update response:", response);
      } else {
        // Create new nota
        console.log("[DEBUG-FRONTEND] Creating new nota");
        const response = await createNota(formData);
        console.log("[DEBUG-FRONTEND] Create response:", response);
        
        // Verify if the factura was incorrectly set to anulada
        if (response && response.facturaId) {
          try {
            // Wait a short delay to allow any backend processes to complete
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Re-fetch the facturas to check the estado
            const facturas = await getFacturas();
            const updatedFactura = facturas.find(f => f.id === response.facturaId);
            
            console.log("[DEBUG-FRONTEND] Verifying factura estado after nota creation:", 
              updatedFactura ? updatedFactura.estado : "Factura not found");
              
            // Alert if the factura was incorrectly set to anulada
            if (updatedFactura && updatedFactura.estado === 'anulada') {
              console.error("[DEBUG-FRONTEND] WARNING: Factura was incorrectly set to anulada after nota creation");
              toast.error(
                "La factura fue marcada como anulada. Esto podría ser un error del sistema. Por favor, verifique el estado de la factura.",
                { duration: 5000 }
              );
            }
          } catch (verifyError) {
            console.error("[DEBUG-FRONTEND] Error verifying factura estado:", verifyError);
          }
        }
      }
      
      toast.success(form.id ? "Nota actualizada con éxito" : "Nota creada con éxito");
      resetForm();
      onClose();
      await onSuccess();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(form.id ? "No se pudo actualizar la nota" : "No se pudo crear la nota");
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={notaToEdit ? "Editar Nota" : "Nueva Nota"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo de Nota <span className="text-red-500">*</span>
              </label>
              <Select
                options={[
                  { value: 'credito', label: 'Nota de Crédito' },
                  { value: 'debito', label: 'Nota de Débito' }
                ]}
                defaultValue={form.tipo}
                onChange={(value) => handleSelectChange(value, 'tipo')}
                className={errors.tipo ? "border-error-500" : ""}
              />
              {errors.tipo && <p className="mt-1 text-xs text-error-500">{errors.tipo}</p>}
              
              <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {form.tipo === 'credito' ? 'Nota de Crédito' : 'Nota de Débito'}: Definición
                </h4>
                {form.tipo === 'credito' ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Nota de Crédito</span>: Documento comercial que indica una <span className="font-medium text-green-600 dark:text-green-400">reducción</span> en el monto adeudado por un cliente o adeudado a un proveedor.
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Casos de uso</span>: Devolución de productos, bienes dañados, errores en la factura, o bonificaciones.
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Efecto en la factura</span>: Reduce el monto total y el saldo pendiente. Si el saldo llega a cero, la factura se marca como "pagada".
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Nota de Débito</span>: Documento comercial usado para <span className="font-medium text-red-600 dark:text-red-400">ajustar hacia arriba</span> el monto de una factura ya emitida o para registrar cargos adicionales.
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Casos de uso</span>: Corrección de facturación insuficiente, cargos adicionales no incluidos, o ajustes en precio.
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Efecto en la factura</span>: Aumenta el monto total y el saldo pendiente. Si la factura estaba pagada, cambiará su estado a "pendiente".
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Número <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="numero"
                placeholder="Número de nota"
                value={form.numero}
                onChange={handleInputChange}
                error={!!errors.numero}
                hint={errors.numero}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                name="fecha"
                placeholder="Fecha"
                value={form.fecha}
                onChange={handleInputChange}
                error={!!errors.fecha}
                hint={errors.fecha}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Monto <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                name="monto"
                placeholder="Monto"
                value={form.monto}
                onChange={handleInputChange}
                min="0"
                error={!!errors.monto}
                hint={errors.monto}
              />
              {selectedFactura && (
                <p className="text-xs text-gray-500 mt-1">
                  Saldo actual de la factura: <span className="font-medium">{selectedFactura.saldoPendiente?.toFixed(2) || '0.00'}</span>
                </p>
              )}
            </div>
          </div>
          
          {/* Right column */}
          <div className="space-y-6">
            <div className="mb-4">
              <label htmlFor="facturaId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Factura <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="facturaSearch"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className={`block w-full rounded-md border ${
                    errors.facturaId ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'
                  } shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-800 dark:text-white`}
                  placeholder="Buscar por número de factura o cliente"
                  readOnly={!!notaToEdit}
                />
                
                {loadingSearch && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 dark:border-primary-400"></div>
                  </div>
                )}
                
                {showSuggestions && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {searchResults.map((result) => (
                        <li 
                          key={result.id}
                          className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between text-gray-800 dark:text-gray-200"
                          onClick={() => handleSelectFactura(result)}
                        >
                          <div className="flex items-center">
                            <span className="font-medium">{result.numero}</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {result.clienteNombre}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {showSuggestions && searchTerm.length >= 2 && searchResults.length === 0 && !loadingSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-2 px-4 text-sm text-gray-500 dark:text-gray-400">
                    No se encontraron coincidencias.
                  </div>
                )}
                
                {errors.facturaId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-500">
                    {errors.facturaId}
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo <span className="text-red-500">*</span>
              </label>
              <Select
                options={[
                  { value: 'error', label: 'Error' },
                  { value: 'descuento', label: 'Descuento' },
                  { value: 'ajuste', label: 'Ajuste' },
                  { value: 'otro', label: 'Otro' }
                ]}
                defaultValue={form.motivo}
                onChange={(value) => handleSelectChange(value, 'motivo')}
                className={errors.motivo ? "border-error-500" : ""}
              />
              {errors.motivo && <p className="mt-1 text-xs text-error-500">{errors.motivo}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descripción <span className="text-red-500">*</span>
              </label>
              <textarea
                name="descripcion"
                value={form.descripcion}
                onChange={handleInputChange}
                className={`appearance-none block w-full px-3 py-2 border ${
                  errors.descripcion ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm`}
                rows={5}
                placeholder="Describa el motivo de la nota de crédito/débito..."
              />
              {errors.descripcion && (
                <p className="mt-1 text-sm text-red-500">{errors.descripcion}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
          <Button
            variant="primary"
            type="submit"
            disabled={isLoading}
            className="sm:col-start-2"
          >
            {isLoading ? "Procesando..." : (notaToEdit ? "Actualizar Nota" : "Crear Nota")}
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={onClose}
            className="sm:col-start-1"
          >
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateNotaModal; 